import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn/components/ui/tabs";
import { Button } from "@shadcn/components/ui/button";
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const StatusSummary = ({
  loading,
  error,
  statusSummaryData,
  statusSummaryData2,
  selectedStatusTab,
  setSelectedStatusTab,
  selectedRegion
}) => {
  // State to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Transform status-grouped data to category-grouped data
  const transformToCategories = (statusData) => {
    if (!statusData || statusData.length === 0) return [];

    const categoryGroups = {};

    // Iterate through each status group
    statusData.forEach(statusGroup => {
      statusGroup.skus.forEach(sku => {
        const category = sku.category || 'Unknown';
        
        if (!categoryGroups[category]) {
          categoryGroups[category] = {
            category,
            skus: [],
            count: 0,
            soh: 0,
            upcomingTotal: 0
          };
        }

        // Add SKU with its status information
        categoryGroups[category].skus.push({
          ...sku,
          status: statusGroup.status // Add status info to each SKU
        });
        
        categoryGroups[category].count += 1;
        categoryGroups[category].soh += sku.soh;
        categoryGroups[category].upcomingTotal += sku.upcoming;
      });
    });

    // Convert to array and calculate percentages
    const totalSkus = Object.values(categoryGroups).reduce((acc, cat) => acc + cat.count, 0);
    
    return Object.values(categoryGroups)
      .map(categoryGroup => ({
        ...categoryGroup,
        percentage: totalSkus > 0 ? ((categoryGroup.count / totalSkus) * 100).toFixed(0) : 0
      }))
      .sort((a, b) => a.category.localeCompare(b.category)); // Sort alphabetically
  };

  const categoryData = useMemo(() => transformToCategories(statusSummaryData), [statusSummaryData]);
  const categoryData2 = useMemo(() => transformToCategories(statusSummaryData2), [statusSummaryData2]);

  // Toggle expand/collapse for a category
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Export functionality
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const currentData = selectedStatusTab === 'formula1' ? categoryData : categoryData2;
      
      if (!currentData || currentData.length === 0) {
        alert('No data to export');
        return;
      }

      // Prepare export data - flatten all SKUs with category info
      const exportData = [];
      currentData.forEach(categoryGroup => {
        categoryGroup.skus.forEach(sku => {
          exportData.push({
            'SKU': sku.sku,
            'Product Name': sku.productName,
            'Category': sku.category,
            'SOH (Current Stock)': sku.soh,
            'Incoming Stock': sku.upcoming,
            'Inventory Status': sku.status
          });
        });
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Create main data sheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 15 }, // SKU
        { wch: 25 }, // Product Name
        { wch: 15 }, // Category
        { wch: 12 }, // SOH
        { wch: 12 }, // Incoming Stock
        { wch: 15 }, // Inventory Status
      ];
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Status Summary');
      
      // Add summary sheet
      const summaryData = currentData.map(cat => ({
        'Category': cat.category,
        'SKU Count': cat.count,
        'SKU Count %': `${cat.percentage}%`,
        'Total SOH': cat.soh,
        'Total Incoming': cat.upcomingTotal
      }));
      
      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      summaryWorksheet['!cols'] = [
        { wch: 15 }, // Category
        { wch: 10 }, // SKU Count
        { wch: 10 }, // SKU Count %
        { wch: 12 }, // Total SOH
        { wch: 12 }, // Total Incoming
      ];
      
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Category Summary');
      
      // Add notes sheet
      const notesData = [
        { 'Notes': 'Stock Status Summary Export' },
        { 'Notes': '' },
        { 'Notes': `Region: ${selectedRegion}` },
        { 'Notes': `Tab: ${selectedStatusTab === 'formula1' ? 'Current Stock' : 'After Stock Arrival'}` },
        { 'Notes': `Export Date: ${new Date().toLocaleDateString()}` },
        { 'Notes': '' },
        { 'Notes': 'Status Definitions:' },
        { 'Notes': '• New: Month Cover = 0' },
        { 'Notes': '• Out of Stock: Month Cover < 0' },
        { 'Notes': '• Shortage: Month Cover < 0.5 months' },
        { 'Notes': '• Fast Moving: Month Cover 0.5-1 month' },
        { 'Notes': '• Ideal: Month Cover 1-3 months' },
        { 'Notes': '• Slow Moving: Month Cover 3-5 months' },
        { 'Notes': '• Excess: Month Cover > 5 months' },
      ];
      
      const notesWorksheet = XLSX.utils.json_to_sheet(notesData);
      notesWorksheet['!cols'] = [{ wch: 50 }];
      
      XLSX.utils.book_append_sheet(workbook, notesWorksheet, 'Notes');
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const tabSuffix = selectedStatusTab === 'formula1' ? 'current-stock' : 'after-arrival';
      const filename = `stock-status-summary_${selectedRegion}_${tabSuffix}_${dateStr}.xlsx`;
      
      // Export the file
      XLSX.writeFile(workbook, filename);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const renderTable = (data, keySuffix) => {
    if (!data || data.length === 0) {
      return <div className="text-center py-6">No data available</div>;
    }

    return (
      <div className="overflow-x-auto pb-4 shadow border rounded-lg">
        <Table className="min-w-max w-full">
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Category</TableHead>
              <TableHead className="text-right font-semibold">Count of SKU</TableHead>
              <TableHead className="text-right font-semibold">SKU Count %</TableHead>
              <TableHead className="text-right font-semibold">SOH</TableHead>
              <TableHead className="text-right font-semibold">Sum of Total Upcoming</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((categoryGroup) => {
              const isExpanded = expandedCategories[categoryGroup.category];
              
              return (
                <React.Fragment key={`${categoryGroup.category}-${keySuffix}`}>
                  {/* Category Header Row */}
                  <TableRow 
                    className="bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => toggleCategory(categoryGroup.category)}
                  >
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 transition-transform" />
                        ) : (
                          <ChevronDown className="h-4 w-4 transition-transform" />
                        )}
                        {categoryGroup.category}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{categoryGroup.count}</TableCell>
                    <TableCell className="text-right font-semibold">{categoryGroup.percentage}%</TableCell>
                    <TableCell className="text-right font-semibold">{categoryGroup.soh.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-semibold">{categoryGroup.upcomingTotal.toLocaleString()}</TableCell>
                  </TableRow>

                  {/* SKU Detail Rows (shown when expanded) */}
                  {isExpanded && (
                    <>
                      {/* Header for SKU details */}
                      <TableRow className="bg-gray-100">
                        <TableCell className="pl-8 font-medium text-sm">SKU Details</TableCell>
                        <TableCell className="text-center font-medium text-sm">Status</TableCell>
                        <TableCell className="text-center font-medium text-sm">Product Name</TableCell>
                        <TableCell className="text-right font-medium text-sm">SOH</TableCell>
                        <TableCell className="text-right font-medium text-sm">Incoming</TableCell>
                      </TableRow>
                      
                      {categoryGroup.skus.map((sku, skuIndex) => (
                        <TableRow 
                          key={`${categoryGroup.category}-${sku.sku}-${keySuffix}`}
                          className="bg-white transition-all duration-200 ease-in-out"
                        >
                          <TableCell className="pl-8 text-gray-600">↳ {sku.sku}</TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(sku.status)}`}>
                              {sku.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">{sku.productName}</TableCell>
                          <TableCell className="text-right">{sku.soh.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{sku.upcoming.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}

                  {/* Show aggregate summary when collapsed */}
                  {!isExpanded && (
                    <TableRow className="bg-blue-50 text-blue-800">
                      <TableCell className="pl-8 text-sm italic">
                        {categoryGroup.count} SKUs (click to expand)
                      </TableCell>
                      <TableCell className="text-gray-500">—</TableCell>
                      <TableCell className="text-gray-500">—</TableCell>
                      <TableCell className="text-gray-500">—</TableCell>
                      <TableCell className="text-gray-500">—</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Helper function to get status badge colors
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Out of Stock': return 'bg-red-100 text-red-800';
      case 'Shortage': return 'bg-orange-100 text-orange-800';
      case 'Fast Moving': return 'bg-yellow-100 text-yellow-800';
      case 'Ideal': return 'bg-green-100 text-green-800';
      case 'Slow Moving': return 'bg-purple-100 text-purple-800';
      case 'Excess': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <h4 className="text-xl font-semibold">Stocks Status Summary</h4>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
            disabled={isExporting || loading}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <span className="mt-2 text-sm text-gray-500">Loading data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-6">
            {error}
          </div>
        ) : (
          <Tabs defaultValue="formula1" value={selectedStatusTab} onValueChange={setSelectedStatusTab} className="w-full">
            <TabsList className="mb-4 grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="formula1">Current Stock</TabsTrigger>
              <TabsTrigger value="formula2">After Stock Arrival</TabsTrigger>
            </TabsList>
            
            <TabsContent value="formula1">
              {renderTable(categoryData, 'f1')}
              
              <div className="mt-4 text-sm text-gray-500">
                <p>Note: This table groups SKUs by category, with expandable sections when a category is clicked. Status is based on the formula: Current Stock / (3 months forecast)</p>
                <ul className="mt-2 list-disc pl-5">
                  <li><span className="font-medium">New</span>: Month Cover = 0</li>
                  <li><span className="font-medium">Out of Stock</span>: Month Cover &lt; 0</li>
                  <li><span className="font-medium">Shortage</span>: Month Cover &lt; 2 Weeks (0.5 months)</li>
                  <li><span className="font-medium">Fast Moving</span>: Month Cover 2-4 Weeks (0.5-1 month)</li>
                  <li><span className="font-medium">Ideal</span>: Month Cover 1-3 months</li>
                  <li><span className="font-medium">Slow Moving</span>: Month Cover 3-5 months</li>
                  <li><span className="font-medium">Excess</span>: Month Cover &gt; 5 months</li>
                </ul>
              </div>
            </TabsContent>
            
            <TabsContent value="formula2">
              {renderTable(categoryData2, 'f2')}
              
              <div className="mt-4 text-sm text-gray-500">
                <p>Note: This table groups SKUs by category, with expandable sections when a category is clicked. Status is based on the formula: (Current Stock + Upcoming - Forecast current month) / (3 months forecast)</p>
                <ul className="mt-2 list-disc pl-5">
                  <li><span className="font-medium">New</span>: Month Cover = 0</li>
                  <li><span className="font-medium">Out of Stock</span>: Month Cover &lt; 0</li>
                  <li><span className="font-medium">Shortage</span>: Month Cover &lt; 2 Weeks (0.5 months)</li>
                  <li><span className="font-medium">Fast Moving</span>: Month Cover 2-4 Weeks (0.5-1 month)</li>
                  <li><span className="font-medium">Ideal</span>: Month Cover 1-3 months</li>
                  <li><span className="font-medium">Slow Moving</span>: Month Cover 3-5 months</li>
                  <li><span className="font-medium">Excess</span>: Month Cover &gt; 5 months</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusSummary;
